import type { ListResult } from 'common/api/v1/types'
import type {
    ApplianceLicensePurchase,
    ConsoleApplianceLicensePurchase,
    ConsoleCustomerRtu,
    CustomerRtu,
} from './types'

type RtuPurchase = Pick<
    CustomerRtu | ConsoleCustomerRtu,
    'id' | 'rtuType' | 'quantity' | 'reference' | 'createdAt' | 'transfer'
>
type AppliancePurchase = Pick<
    ApplianceLicensePurchase | ConsoleApplianceLicensePurchase,
    'id' | 'productCode' | 'quantity' | 'createdAt' | 'netsuiteOrder' | 'transfer'
>
type CsvAudience = 'billing' | 'console'
type LicenseTransfer = NonNullable<AppliancePurchase['transfer'] | RtuPurchase['transfer']>

/**
 * Describes one side of a license transfer. Only the Billing Manager names the other customer; Console
 * responses never carry it.
 */
export const licenseTransferLabel = (transfer: LicenseTransfer, audience: CsvAudience): string => {
    const serialNumber = 'serialNumber' in transfer ? transfer.serialNumber : undefined
    if (audience === 'billing' && 'customer' in transfer) {
        const moved = `Transferred ${transfer.direction === 'in' ? 'from' : 'to'} ${transfer.customer.name}`
        return serialNumber ? `${moved} with appliance ${serialNumber}` : moved
    }
    return serialNumber
        ? `Appliance ${serialNumber} transferred ${transfer.direction}`
        : `Transferred ${transfer.direction}`
}

const pageSize = 500

/** Loads every purchase page so an export is not limited to the first page shown in the UI. */
export const listAllPurchasePages = async <T>(
    fetchPage: (skip: number, limit: number) => Promise<ListResult<T>>
): Promise<T[]> => {
    const purchases: T[] = []
    while (true) {
        const page = await fetchPage(purchases.length, pageSize)
        purchases.push(...page.items)
        if (purchases.length >= page.total) return purchases
        if (page.items.length === 0) throw new Error('Could not load all purchases.')
    }
}

/** Quotes CSV fields and prevents spreadsheet applications from evaluating user-entered references. */
const csvCell = (value: string | number): string => {
    const text = String(value)
    const safe = typeof value === 'string' && /^\s*[=+\-@]/u.test(text) ? `'${text}` : text
    return `"${safe.replaceAll('"', '""')}"`
}

const manualOrigin = (audience: CsvAudience) =>
    audience === 'billing' ? 'Added in billing server' : 'Added by Net Insight'

export const formatRtuPurchaseCsv = (purchases: RtuPurchase[], audience: CsvAudience): string => {
    const rows: (string | number)[][] = [
        ['Transaction ID', 'RTU type', 'Quantity', 'Reference', 'Purchased (UTC)', 'Origin'],
        ...purchases.map((purchase) => [
            purchase.id,
            purchase.rtuType.name,
            purchase.quantity,
            purchase.reference,
            new Date(purchase.createdAt).toISOString(),
            purchase.transfer ? licenseTransferLabel(purchase.transfer, audience) : manualOrigin(audience),
        ]),
    ]
    return formatRows(rows)
}

const formatRows = (rows: (string | number)[][]): string =>
    rows.map((row) => row.map(csvCell).join(',')).join('\r\n') + '\r\n'

/** Includes transaction provenance without exposing internal order numbers or transfer counterparties in Console. */
const appliancePurchaseOrigin = ({ transfer, netsuiteOrder }: AppliancePurchase, audience: CsvAudience): string => {
    if (transfer) return licenseTransferLabel(transfer, audience)
    if (netsuiteOrder) {
        let orderNumber = netsuiteOrder.purchaseOrderNumber ?? '—'
        if (audience === 'billing' && 'salesOrderNumber' in netsuiteOrder) {
            orderNumber = netsuiteOrder.salesOrderNumber
        }
        return `Order ${orderNumber}`
    }
    return manualOrigin(audience)
}

export const formatApplianceLicensePurchaseCsv = (
    purchases: AppliancePurchase[],
    productNames: Record<string, string>,
    audience: CsvAudience
): string =>
    formatRows([
        ['Transaction ID', 'Product code', 'Product', 'Quantity', 'Transaction date (UTC)', 'Origin'],
        ...purchases.map((purchase) => [
            purchase.id,
            purchase.productCode,
            productNames[purchase.productCode] ?? purchase.productCode,
            purchase.quantity,
            new Date(purchase.createdAt).toISOString(),
            appliancePurchaseOrigin(purchase, audience),
        ]),
    ])
